import { useRef, type ReactNode } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import { type DisplayCombatant, type EncounterState, type FrozenNpcFeature, type WeaponProfile, heldAction } from "@lancer-vtt/domain";
import { isMaskedCombatant } from "../disclosure";
import { buildNpcDossierSections, lockedFeatureCount, type NpcDossierSectionKind, type NpcDossierSectionState } from "../npcDossier";
import { dossierLoadout } from "../unitLoadout";
import type { UseEncounter } from "../useEncounter";
import { PreparedHoldPanel } from "./inspectorPanels";
import { GrantedTurnStrip } from "./GrantedTurnStrip";
import { TurnStateText } from "./TurnControls";
import { UnitLoadout } from "./UnitLoadout";
import { UnitSheet, UnitSheetStatuses, type UnitSheetIdentity } from "./UnitSheet";
import { descriptiveStatCells, HaseStatRow, maskedStatCells, StatStrip } from "./unitStats";
import { MaskedSheetVitals, npcVitalTracks, UnitSheetVitals } from "./unitVitals";

/** The dossier sections whose unlock flashes the loadout. */
const LOADOUT_SECTIONS: readonly NpcDossierSectionKind[] = ["threat", "traits", "systems", "reactions", "tech"];

/**
 * The **player's view of a hostile NPC**: the same {@link UnitSheet} card the GM reads, masked to
 * the disclosure tier (`unit-sheet-redesign` Phase 5). Routed from {@link InspectorBody} for
 * `kind === "npc"` whenever `mountsNpcControl` is false: a hostile the viewer does not control, and
 * one it controls without the visibility grant (`decisions/0179`), where the state line carries the
 * granted turn strip. The GM, and a player who controls the NPC and receives it unmasked, get
 * {@link NpcInspectorBody}. A mech or pilot moved to the npc side reaches it the same way when the
 * player receives it masked or HP-hidden. The GM's REVEAL tray also renders it, as AS PLAYERS SEE IT, from the
 * projection a player with no slot gets, with a `band` and the GM strip kept.
 *
 * What each tier shows, all decided by {@link buildNpcDossierSections}; this component never
 * re-derives a tier:
 * - **`"class"`**: the header (name, class, tier, templates, terse line), hatched vitals reading
 *   `?? / ??`, stats reading `??` but for SIZE, the statuses, an ABOUT row (class flavor and template
 *   text, public per RAW p.283), the rows of any feature the unit has revealed by using it
 *   (`decisions/0207`), and one locked row, `N FEATURES NOT SEEN · HACK › SCAN REVEALS`, whose count
 *   is the model's and counts only what is still locked.
 * - **`"loadout"`**: adds the weapons, traits, systems, tech and reactions as loadout rows; the
 *   vitals and stats stay masked.
 * - **`"full"`**: the vitals (HP `?? / max` while `showEnemyHp` is off, `decisions/0082`), the stat
 *   grid and the HASE row, and the read-only prepared hold.
 *
 * The rows carry no live state pills at any tier (see {@link dossierLoadout}). When a section
 * unlocks on a render (the GM's disclosure change or a player's Scan), the block it fills plays one
 * short flash.
 *
 * **Explicitly absent, at every tier**: the REACTION column, the −/+ steppers, the dice buttons,
 * TurnControls' END TURN (except the granted fold's), {@link AdvisorPanel},
 * {@link AutoplayControls}, {@link DisclosureControl} and {@link ConcealToggle}. None carries
 * player-facing intel or is a player's to touch. The granted strip is this body's only dispatch.
 *
 * **The masking guard.** This takes the branded {@link DisplayCombatant} unchanged: no second
 * masking mechanism, no `state.combatants` read. Every field read below either comes off
 * `MaskedKeepFields` (present at every tier) or sits behind an {@link isMaskedCombatant} /
 * `disclosure` narrowing that mirrors the shape {@link projectCombatantFor} produces, so an illegal
 * read (`c.current` while masked) is a compile error, not a runtime guess. The render test
 * (`PlayerNpcInspectorBody.render.test.tsx`) pins that with `@ts-expect-error`.
 */
export function PlayerNpcInspectorBody({
  combatant: c,
  identity,
  state,
  compendium,
  granted,
  band,
  gm,
}: {
  /** The branded {@link DisplayCombatant} — see {@link InspectorBody}. */
  combatant: DisplayCombatant;
  /** The sheet header's identity, derived once by {@link InspectorBody}. Public at every tier. */
  identity: UnitSheetIdentity;
  /** Read for the state line and {@link heldAction} (the PREPARED telegraph). */
  state: EncounterState;
  compendium: Compendium;
  /**
   * Set when the viewer controls this NPC without the visibility grant (`decisions/0179`): the
   * state line then carries {@link GrantedTurnStrip}'s note, ACTIVATE and END TURN.
   */
  granted: { actorId: string; encounter: UseEncounter } | null;
  /** The GM's AS PLAYERS SEE IT preview only: the band saying so, and the GM strip kept under the card. */
  band?: ReactNode;
  gm?: ReactNode;
}) {
  // Each section's state as of the LAST render, mutated during render (a ref, never a store), so
  // `justRevealed` is exactly the sections that flipped `"locked"` → `"revealed"` on THIS render.
  // The flash is one CSS keyframe class that React drops again on the next render; no timer.
  const priorSectionStates = useRef<Partial<Record<NpcDossierSectionKind, NpcDossierSectionState>>>({});

  // `getById`, never `requireById`: no NPC pack ships by default, and a missing lookup must degrade.
  const npcClass = c.sourceRef ? compendium.getById("npc_class", c.sourceRef) : undefined;
  const sections = buildNpcDossierSections(c, npcClass);
  const justRevealed = new Set<NpcDossierSectionKind>();
  for (const s of sections) {
    if (priorSectionStates.current[s.kind] === "locked" && s.state === "revealed") justRevealed.add(s.kind);
    priorSectionStates.current[s.kind] = s.state;
  }
  const flash = (on: boolean) => (on ? "unit-sheet-reveal" : undefined);

  // The rows are whatever the projection kept: every weapon and feature from `"loadout"` on, and at
  // `"class"` only those the unit revealed by using them (`decisions/0207`), whole, beside the
  // locked row's count of the rest.
  const weapons: readonly WeaponProfile[] = c.base.weapons ?? [];
  const features: readonly FrozenNpcFeature[] = c.base.npcFeatures ?? [];
  // A row revealed by use on THIS render flashes the loadout block like a section unlock does.
  const priorRows = useRef<number | undefined>(undefined);
  const rowsGrew = priorRows.current !== undefined && weapons.length + features.length > priorRows.current;
  priorRows.current = weapons.length + features.length;
  const locked = lockedFeatureCount(sections);

  // PREPARED telegraphs at `"full"` only: `heldAction` needs the activation state this view only
  // trusts once the unit is unmasked. Read-only here, never RELEASE NOW / DROP; a player
  // controlling the unit without EYES gets no RELEASE control either, which `decisions/0179`
  // accepts: control without visibility cannot fire.
  const held = !isMaskedCombatant(c) ? heldAction(state.activation, c.id) : null;

  return (
    <UnitSheet
      identity={identity}
      className="unit-sheet-dossier"
      band={band}
      gm={gm}
      hold={held && <PreparedHoldPanel held={held} weapons={weapons} releaseArmed={false} canControl={false} onArmRelease={() => {}} onDrop={() => {}} />}
      stateLine={
        <>
          <TurnStateText combatantId={c.id} state={state} />
          {granted && <GrantedTurnStrip combatantId={c.id} state={state} actorId={granted.actorId} encounter={granted.encounter} />}
        </>
      }
    >
      <div className={flash(justRevealed.has("vitals"))}>
        {isMaskedCombatant(c) ? (
          <>
            <MaskedSheetVitals />
            <StatStrip cells={maskedStatCells(c.base)} className="is-masked" onOpenDice={null} />
          </>
        ) : (
          <>
            <UnitSheetVitals {...npcVitalTracks(c)} onSetTrack={null} disabled />
            <StatStrip cells={descriptiveStatCells(c.base, { grit: false })} onOpenDice={null} />
            <HaseStatRow base={c.base} onOpenDice={null} />
          </>
        )}
      </div>

      <UnitSheetStatuses combatant={c} edit={null} />

      <div className={flash(rowsGrew || LOADOUT_SECTIONS.some((k) => justRevealed.has(k)))}>
        <UnitLoadout
          groups={dossierLoadout({ combatant: c, weapons, features, compendium })}
          locked={locked > 0 ? `${locked} ${locked === 1 ? "FEATURE" : "FEATURES"} NOT SEEN · HACK › SCAN REVEALS` : undefined}
        />
      </div>
    </UnitSheet>
  );
}
